import React, { useState } from "react";
import {
  StyleSheet,
  TouchableOpacity,
  View,
  Platform,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { router } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "../ThemedText";
import { useThemeColor } from "@/hooks/useThemeColor";
import { ITagAssociationMeta, ITag, TagType } from "common247";

import { ThemedChip } from "../ThemedChip";

export interface IThemedTagProps {
  /** The tag data or association metadata */
  tag: ITagAssociationMeta | ITag;
  /** Custom click handler (defaults to deep-link navigation) */
  onPress?: (tag: ITagAssociationMeta | ITag) => void;
  /** Callback triggered when user clicks the "x" remove button */
  onRemove?: (tag: ITagAssociationMeta | ITag) => void;
  /** Whether the user has permission to remove this tag */
  canRemove?: boolean;
  /** Compact density rendering */
  compact?: boolean;
  /** Custom container style */
  style?: StyleProp<ViewStyle>;
  /** Disable press interaction */
  readOnly?: boolean;
}

/**
 * Returns visual color tokens and icons for each TagType.
 * STRICTLY respects the Purple Ban: Uses Neutral/Slate, Emerald, Blue, Rose, Amber, Teal.
 */
export function getTagVisualConfig(type: TagType | string, isDark: boolean) {
  const normalized = (type || "general").toLowerCase();

  switch (normalized) {
    case "user":
    case "contact":
      return {
        icon: "person-circle-outline" as const,
        prefix: "@",
        borderColor: isDark ? "rgba(52, 211, 153, 0.45)" : "rgba(5, 150, 105, 0.35)",
        bgColor: isDark ? "rgba(52, 211, 153, 0.14)" : "rgba(5, 150, 105, 0.09)",
        textColor: isDark ? "#34D399" : "#047857",
        badgeText: "Contact",
      };
    case "group":
      return {
        icon: "people-outline" as const,
        prefix: "@",
        borderColor: isDark ? "rgba(230, 224, 168, 0.55)" : "#C5B866",
        bgColor: isDark ? "rgba(230, 224, 168, 0.20)" : "#F7F2C8",
        textColor: isDark ? "#E6E0A8" : "#6B5E00",
        badgeText: "Group",
      };
    case "memory":
      return {
        icon: "sparkles-outline" as const,
        prefix: "#",
        borderColor: isDark ? "rgba(244, 63, 94, 0.45)" : "rgba(225, 29, 72, 0.35)",
        bgColor: isDark ? "rgba(244, 63, 94, 0.14)" : "rgba(225, 29, 72, 0.09)",
        textColor: isDark ? "#FB7185" : "#BE123C",
        badgeText: "Memory",
      };
    case "media":
      return {
        icon: "images-outline" as const,
        prefix: "+",
        borderColor: isDark ? "rgba(251, 191, 36, 0.45)" : "rgba(217, 119, 6, 0.35)",
        bgColor: isDark ? "rgba(251, 191, 36, 0.14)" : "rgba(217, 119, 6, 0.09)",
        textColor: isDark ? "#FBBF24" : "#B45309",
        badgeText: "Media",
      };
    case "system":
      return {
        icon: "hardware-chip-outline" as const,
        prefix: "*",
        borderColor: isDark ? "rgba(45, 212, 191, 0.45)" : "rgba(13, 148, 136, 0.35)",
        bgColor: isDark ? "rgba(45, 212, 191, 0.14)" : "rgba(13, 148, 136, 0.09)",
        textColor: isDark ? "#2DD4BF" : "#0D9488",
        badgeText: "System",
      };
    case "general":
    default:
      return {
        icon: "pricetag-outline" as const,
        prefix: "#",
        borderColor: isDark ? "rgba(148, 163, 184, 0.4)" : "rgba(100, 116, 139, 0.3)",
        bgColor: isDark ? "rgba(148, 163, 184, 0.12)" : "rgba(100, 116, 139, 0.08)",
        textColor: isDark ? "#94A3B8" : "#475569",
        badgeText: "Tag",
      };
  }
}

/**
 * ThemedTag component representing an entity or general tag.
 * Delegates directly to the platform standard ThemedChip component.
 */
export function ThemedTag({
  tag,
  onPress,
  onRemove,
  canRemove = false,
  compact = false,
  style,
  readOnly = false,
}: IThemedTagProps) {
  const tagType: TagType = ((tag as any)?.tagType || (tag as any)?.type || "general") as TagType;
  const tagId: string = (tag as any)?.tagId || (tag as any)?.id || (typeof tag === "string" ? tag : "unknown");
  const displayValue: string = (tag as any)?.value || (typeof tag === "string" ? tag : (tag as any)?.tagId || "Untagged");
  const tagObj: ITagAssociationMeta = {
    tagId,
    addedAt: (tag as any)?.addedAt || 0,
    addedBy: (tag as any)?.addedBy || "",
  };

  const handlePress = (e?: any) => {
    e?.stopPropagation?.();
    if (readOnly) return;
    if (onPress) {
      onPress(tagObj);
      return;
    }

    // Default tag navigation: filter memories by this tag value
    try {
      router.push({
        pathname: "/my-memories",
        params: { tag: displayValue },
      } as any);
    } catch (err) {
      console.warn(`[ThemedTag] Failed to navigate for tag ${tagId}:`, err);
    }
  };

  const handleRemove = (e?: any) => {
    e?.stopPropagation?.();
    if (onRemove) {
      onRemove(tagObj);
    }
  };

  const showRemoveButton = Boolean(onRemove && canRemove && !readOnly);

  return (
    <ThemedChip
      entityType="tag"
      tagType={tagType}
      id={tagId}
      label={displayValue}
      compact={compact}
      readOnly={readOnly}
      canRemove={showRemoveButton}
      onRemove={showRemoveButton ? handleRemove : undefined}
      onPress={handlePress}
      style={style}
    />
  );
}

const styles = StyleSheet.create({
  outerWrapper: {
    position: "relative",
    flexDirection: "row",
    alignItems: "center",
    marginRight: 6,
    marginVertical: 3,
  },
  outerWrapperCompact: {
    marginRight: 4,
    marginVertical: 2,
  },
  chipContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 14,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  chipContainerWithRemove: {
    paddingRight: 16,
  },
  compactContainer: {
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 2,
    paddingRight: 14,
  },
  icon: {
    marginRight: 4,
  },
  label: {
    fontSize: 12,
    fontWeight: "600",
    letterSpacing: 0.1,
  },
  labelCompact: {
    fontSize: 11,
  },
  removeButton: {
    position: "absolute",
    top: -3,
    right: -3,
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
  },
});

export default ThemedTag;

